import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateClassPasswordApi,
  useRedeemAccessCodeApi,
} from 'eds-common/api/v1';
import {
  getSubscriptionsWithStatusApiQueryKey,
  useGetMeApi,
} from 'eds-common/api/v2';
import { ApiError } from 'eds-common';
import { toast } from '@scholastic/mockingjay';
import { useAccessCodeRedemptionContext } from './AccessCodeRedemptionContext';

export function useConfirmRedemptionSubmit() {
  const queryClient = useQueryClient();
  const { data: user } = useGetMeApi();
  const { goToNextStep, redemptionData, updateRedemptionData } =
    useAccessCodeRedemptionContext();
  const { mutateAsync: createClassPassword } = useCreateClassPasswordApi();
  const { mutateAsync: redeemAccessCode, isPending } = useRedeemAccessCodeApi();

  const handleSubmit = useCallback(async () => {
    if (!redemptionData.product || !user?.userId) {
      return;
    }

    if (user.userSource === 'MyScholastic') {
      try {
        await createClassPassword();
      } catch (error) {
        console.error('Error creating class password:', error);
        return;
      }
    }

    try {
      const redeemedAccessCode = await redeemAccessCode({
        accessCode: redemptionData.accessCode,
        userId: user.userId,
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['applications'] }),
        queryClient.invalidateQueries({
          queryKey: getSubscriptionsWithStatusApiQueryKey(),
        }),
      ]);

      updateRedemptionData({ redeemedAccessCode });
    } catch (error) {
      if (error instanceof ApiError) {
        const { errorBody } = (
          error as ApiError<{ code: string; detailedMessage: string }>
        ).cause;

        if (errorBody?.code === 'user.entitlement.already.exists') {
          toast.error(
            'You have already redeemed this access code. Please check your account for the product.'
          );
          return;
        }

        toast.error(
          'An error occurred while redeeming the access code. Please try again.'
        );
      }

      console.error('Error redeeming access code:', error);
      return;
    }

    goToNextStep();
  }, [
    createClassPassword,
    goToNextStep,
    queryClient,
    redeemAccessCode,
    redemptionData.accessCode,
    redemptionData.product,
    updateRedemptionData,
    user?.userId,
    user?.userSource,
  ]);

  return { handleSubmit, isPending };
}
